import h from "./html";
import Db, { ColumnType } from "./db";
import DatePicker from "./DatePicker";
import Filters from "./Filters";
import {
  iDateLocal,
  iDateTimeLocal,
  iDateToIsoDateTime,
  isoDateToIDate,
  jsDateToLocalIDate,
  pad,
} from "./dates";

interface EventRow {
  calendarId: ColumnType;
  entryId: ColumnType;
  description: ColumnType;
  dt: string;
  dtEnd: string;
  dtstamp: ColumnType;
  summary: ColumnType;
  uid: ColumnType;
  location: ColumnType;
  isOverride: ColumnType;
  isRecurring: ColumnType;
}

const AgendaEvent = ({ event, day }: { event: EventRow; day: string }) => {
  const startDay = iDateLocal(event.dt);
  const endDay = iDateLocal(event.dtEnd);

  const startDateFormatted = new Date(
    iDateToIsoDateTime(event.dt),
  ).toLocaleTimeString(undefined, { timeStyle: "short" });

  const endDateFormatted = new Date(
    iDateToIsoDateTime(event.dtEnd),
  ).toLocaleTimeString(undefined, { timeStyle: "short" });

  let rangeFormatted = "";
  if (startDay === day && endDay === day) {
    rangeFormatted = `${startDateFormatted} - ${endDateFormatted}`;
  } else if (startDay === day && !iDateTimeLocal(event.dt).endsWith("000000")) {
    rangeFormatted = `starts ${startDateFormatted}`;
  } else if (endDay === day) {
    rangeFormatted = `until ${endDateFormatted}`;
  } else {
    rangeFormatted = `all day`;
  }

  return (
    <div className="mb-4 ml-2 border-l-2 border-gray-500 pl-2">
      <div className="text-sm text-gray-500">{rangeFormatted}</div>
      <h3 className="text-lg">{event.summary}</h3>
    </div>
  );
};

export default function IcsView({ db }: { db: Db }) {
  const currentCalendarIds = h.localStorageState("currentCalendars", "[]");
  const today = new Date();
  const tomorrow = new Date(today.valueOf() + 1000 * 60 * 60 * 24);

  const dtStart = h.queryParameterState("dtStart", jsDateToLocalIDate(today));
  const dtEnd = h.queryParameterState("dtEnd", jsDateToLocalIDate(tomorrow));
  const rows = db.query(
    /* sql */ `
      WITH
        calendar_ids AS (
          SELECT
            value AS calendar_id
          FROM
            json_each(?1)
        ),
        resolved_instance AS (
          SELECT
            rank() OVER (
              PARTITION BY
                uid,
                coalesce(recurrence_id, instance)
              ORDER BY
                recurrence_id IS NOT NULL DESC
            ) AS canonical_instance_rank,
            app_event.calendar_id AS calendar_id,
            app_event.entry_id AS entry_id,
            description,
            CASE
              WHEN recurrence_id IS NOT NULL THEN app_event.dtstart
              ELSE coalesce(instance, app_event.dtstart)
            END AS dt,
            dtstart_tz,
            CASE
              WHEN recurrence_id IS NOT NULL THEN app_event.dtend
              WHEN instance IS NOT NULL THEN date_add (instance, app_event.dtstart, app_event.dtend)
              ELSE app_event.dtend
            END AS dtend,
            dtend_tz,
            dtstamp,
            summary,
            uid,
            location,
            recurrence_id IS NOT NULL AS is_override,
            coalesce(instance, recurrence_id) IS NOT NULL AS is_recurring,
            app_exdate.exdate IS NOT NULL AS is_excluded
          FROM
            calendar_ids
            INNER JOIN app_event USING (calendar_id)
            LEFT JOIN rrule_instance ON app_event.rrule = rrule_instance.rrule
            AND rrule_instance.dtstart = app_event.dtstart
            AND rrule_instance.instance <= ?3
            LEFT JOIN app_exdate ON app_exdate.entry_id = app_event.entry_id
            AND app_exdate.calendar_id = app_event.calendar_id
            AND app_exdate.exdate = instance
        ),
        timezone AS (
          SELECT
            *
          FROM
            (
              SELECT
                app_timezone.calendar_id AS calendar_id,
                tzid,
                instance AS tzstart,
                coalesce(
                  lead(instance) OVER (
                    ORDER BY
                      instance
                  ),
                  ?3
                ) AS tzend,
                tzname,
                tzoffsetto AS tzoffset
              FROM
                calendar_ids
                INNER JOIN app_timezone ON app_timezone.calendar_id = calendar_ids.calendar_id
                LEFT JOIN app_timezone_variant ON app_timezone_variant.calendar_id = app_timezone.calendar_id
                AND app_timezone_variant.parent_id = app_timezone.entry_id
                LEFT JOIN rrule_instance ON rrule_instance.dtstart = app_timezone_variant.dtstart
                AND rrule_instance.rrule = app_timezone_variant.rrule
                AND rrule_instance.instance <= ?3
              ORDER BY
                instance
            )
          WHERE
            tzend >= ?2
        )
      SELECT
        resolved_instance.calendar_id AS calendar_id,
        entry_id,
        description,
        to_utc (dt, start_timezone.tzoffset) AS dt_start,
        dtstart_tz,
        to_utc (dtend, end_timezone.tzoffset) AS dt_end,
        dtend_tz,
        dtstamp,
        summary,
        uid,
        location,
        is_override,
        is_recurring,
        start_timezone.tzname AS tzname
      FROM
        resolved_instance
        LEFT JOIN timezone AS start_timezone ON start_timezone.calendar_id = resolved_instance.calendar_id
        AND start_timezone.tzid = resolved_instance.dtstart_tz
        AND start_timezone.tzstart <= dt
        AND start_timezone.tzend >= dt
        LEFT JOIN timezone AS end_timezone ON end_timezone.calendar_id = resolved_instance.calendar_id
        AND end_timezone.tzid = resolved_instance.dtend_tz
        AND end_timezone.tzstart <= dtend
        AND end_timezone.tzend >= dtend
      WHERE
        (
          dt >= ?2
          OR dt < ?2
          AND dtend > ?2
        )
        AND dt < ?3
        AND canonical_instance_rank = 1
      ORDER BY
        dt
    `,
    [
      "calendarId",
      "entryId",
      "description",
      "dt",
      "dtTimezone",
      "dtEnd",
      "dtEndTimezone",
      "dtstamp",
      "summary",
      "uid",
      "location",
      "isOverride",
      "isRecurring",
      "tzName",
    ],
    () => [currentCalendarIds.val, dtStart.val, dtEnd.val],
  ) as h.State<"loading" | Array<EventRow>>;

  return (
    <div className="mx-auto max-w-5xl rounded-lg p-4 pb-0 overflow-scroll sm:h-auto w-screen sm:w-auto grow">
      <Filters
        db={db}
        dtStart={dtStart}
        dtEnd={dtEnd}
        selectedCalendars={currentCalendarIds}
      />
      <DatePicker dtStart={dtStart} dtEnd={dtEnd} />
      {() => {
        if (rows.val === "loading") {
          return "";
        }
        const days = new Map<string, Array<EventRow>>();
        const nextDay = (day: string) => {
          const jsDay = new Date(iDateToIsoDateTime(day));
          jsDay.setDate(jsDay.getDate() + 1);
          return isoDateToIDate(jsDay.toISOString());
        };
        for (let day = dtStart.val; day < dtEnd.val; day = nextDay(day)) {
          days.set(day, []);
        }
        for (const row of rows.val) {
          let startDayLocal = jsDateToLocalIDate(
            new Date(iDateToIsoDateTime(row.dt)),
          );
          let endDayLocal = jsDateToLocalIDate(
            new Date(iDateToIsoDateTime(row.dtEnd)),
          );
          for (
            let day = startDayLocal;
            day <= endDayLocal;
            day = nextDay(day)
          ) {
            days.get(day)?.push(row);
          }
        }

        const renderedRows: Array<any> = [];
        for (const [day, events] of days) {
          renderedRows.push(
            <h2 className="text-2xl text-gray-500">
              {new Date(iDateToIsoDateTime(day)).toLocaleString(undefined, {
                dateStyle: "full",
              })}
              {day === jsDateToLocalIDate(today) ? " (Today)" : ""}
              {day === jsDateToLocalIDate(tomorrow) ? " (Tomorrow)" : ""}
            </h2>,
          );
          let prevEvent: EventRow | null = null;
          for (const event of events) {
            if (prevEvent) {
              const onSameDay =
                iDateLocal(prevEvent.dtEnd) === iDateLocal(event.dt);
              const gap =
                (new Date(iDateToIsoDateTime(event.dt)).getTime() -
                  new Date(iDateToIsoDateTime(prevEvent.dtEnd)).getTime()) /
                1000 /
                60;
              if (gap > 0 && onSameDay) {
                const hours = Math.floor(gap / 60);
                const minutes = gap % 60;
                renderedRows.push(
                  <div className="mb-4 ml-2 text-sm italic text-gray-500">
                    {hours > 0 && minutes > 0
                      ? `${hours} ${hours === 1 ? "hour" : "hours"} ${pad(minutes)} minutes free`
                      : hours > 0
                        ? `${hours} ${hours === 1 ? "hour" : "hours"} free`
                        : `${gap} ${minutes === 1 ? "minute" : "minutes"} free`}
                  </div>,
                );
              }
            }
            renderedRows.push(<AgendaEvent event={event} day={day} />);
            prevEvent = event;
          }
          if (events.length === 0) {
            renderedRows.push(
              <div className="mb-4 ml-2 text-sm italic text-gray-500">
                no events
              </div>,
            );
          }
        }

        return <div>{renderedRows}</div>;
      }}
      <div
        className={() =>
          `flex w-full text-center ${rows.val === "loading" ? "hidden" : ""}`
        }
      >
        <a
          href="javascript:void(0)"
          className="text-xl text-blue-500 underline"
          onclick={(ev: MouseEvent) => {
            ev.preventDefault();
            const end = new Date(iDateToIsoDateTime(dtStart.val));
            const start = new Date(end.valueOf() - 1000 * 60 * 60 * 24);
            dtStart.val = jsDateToLocalIDate(start);
            dtEnd.val = jsDateToLocalIDate(end);
          }}
        >
          ← Previous
        </a>
        <div className="grow" />
        <a
          href="javascript:void(0)"
          className="text-xl text-blue-500 underline"
          onclick={(ev: MouseEvent) => {
            ev.preventDefault();
            const start = new Date(iDateToIsoDateTime(dtEnd.val));
            const end = new Date(start.valueOf() + 1000 * 60 * 60 * 24);
            dtStart.val = jsDateToLocalIDate(start);
            dtEnd.val = jsDateToLocalIDate(end);
          }}
        >
          Next →
        </a>
      </div>
    </div>
  );
}
